import { Injectable, NgZone } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { fromEvent, merge, Subject, Subscription, timer } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class IdleService {
    private minutesTimeout: number = 10;
    private idleTimeout: number = this.minutesTimeout * 60 * 1000;
    private warningTimeout: number = (this.minutesTimeout - 1) * 60 * 1000;
    public onIdleWarning: Subject<void> = new Subject<void>();
    public onIdleTimeout: Subject<void> = new Subject<void>();

    private idleWarningSubscription: Subscription;
    private idleTimeoutSubscription: Subscription;
    private warningDialog: boolean = false;
    private areEventsActive: boolean = true;
    private lastActivityTimestamp: number;

    constructor(private ngZone: NgZone, private keycloakService: KeycloakService) {
        this.initListener();
    }

    private initListener() {
        this.ngZone.runOutsideAngular(() => {
            merge(
                fromEvent(document, 'mousedown'),
                fromEvent(document, 'touchstart').pipe(
                    debounceTime(300),
                ),
                fromEvent(document, 'scroll').pipe(
                    debounceTime(300),
                ),
                fromEvent(document, 'mousemove').pipe(
                    debounceTime(300),
                ),
                fromEvent(document, 'keydown')
            ).subscribe(() => {
                if (this.areEventsActive) {
                    this.extendSession();
                }
            });
        });
    }

    private initTimer() {
        const currentTime = Date.now();
        const warningTimeLeft = this.warningTimeout - (currentTime - this.lastActivityTimestamp);
        const timeoutTimeLeft = this.idleTimeout - (currentTime - this.lastActivityTimestamp);

        if (warningTimeLeft > 0) {
            this.idleWarningSubscription = timer(warningTimeLeft).subscribe(() => {
                this.ngZone.run(() => {
                    this.onIdleWarning.next();
                    this.disableUserActivityEvents();
                    this.warningDialog = true;
                });
            });
        }

        if (timeoutTimeLeft > 0) {
            this.idleTimeoutSubscription = timer(this.idleTimeout).subscribe(() => {
                this.ngZone.run(() => {
                    this.onIdleTimeout.next();
                    this.keycloakService.clearToken();
                });
            });
        }
        
        this.checkForTimeDrift();
    }

    private checkForTimeDrift() {
        const intervalTime = 10000;
        const checkInterval = timer(intervalTime, intervalTime).subscribe(() => {
            const currentTime = Date.now();
            const timeElapsedSinceLastActivity = currentTime - this.lastActivityTimestamp;
            if (timeElapsedSinceLastActivity >= this.idleTimeout) {
                this.ngZone.run(() => {
                    this.onIdleTimeout.next();
                    this.keycloakService.clearToken();
                });
                checkInterval.unsubscribe();
            }
        });
    }

    public startTimer(): void {
        this.lastActivityTimestamp = Date.now();
        this.initTimer();
    }

    private resetTimers() {
        if (this.idleWarningSubscription) {
            this.idleWarningSubscription.unsubscribe();
        }
        if (this.idleTimeoutSubscription) {
            this.idleTimeoutSubscription.unsubscribe();
        }
        this.lastActivityTimestamp = Date.now();
        this.initTimer();
    }

    public extendSession() {
        this.resetTimers();
        if (this.warningDialog) {
            this.warningDialog = false;
            this.enableUserActivityEvents();
        }
        this.keycloakService.updateToken(60).catch(() => {
            this.keycloakService.login();
        });
    }

    private disableUserActivityEvents() {
        this.areEventsActive = false;
    }

    private enableUserActivityEvents() {
        this.areEventsActive = true;
    }
}